import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material'
import { StyledMenu } from 'components/StyledMenu/StyledMenu'
import ChevronUp from 'assets/chevron-up.svg'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useLayout } from 'hooks/useLayout'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useSetLe } from 'stores/useSetLe'
import { useSetMe } from 'stores/useMeQuery'
import SearchIcon from '@mui/icons-material/Search'

export const CorporateAccountDetails = () => {
  const navigate = useNavigate()
  const { isMobile } = useLayout()
  const qc = useQueryClient()
  const { passedMe } = useSetMe()
  const { data, refetch } = passedMe
  const { currentCorporateAccountName, setCurrentCorporateAccount } =
    useCurrentCorporateAccountStore()
  const { currentCorporateAccountId } = useCurrentCorporateAccountStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [showAllAccounts, setShowAllAccounts] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const open = Boolean(anchorEl)
  const setPassedLe = useSetLe((state) => state.setPassedLe)

  const hasNoAccess =
    !data?.corporateAccounts || data?.corporateAccounts.length === 0

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    setSearchTerm('') // clear search when menu closes
  }, [])

  const toggleMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(open ? null : event.currentTarget)
      setShowAllAccounts(currentCorporateAccountName !== null)
    },
    [open, currentCorporateAccountName]
  )

  useEffect(() => {
    if (open) {
      void refetch()
    }
  }, [open, refetch])

  const handleAccountSelect = useCallback(
    async (accountId: number) => {
      const enrollSweestakeLocationRoute =
        globalThis.location.href.split('/')[5]
      if (
        [
          'EnrollLocations',
          'AdditionalEnrollmentDetails',
          'ReviewLocationsToEnroll',
        ].includes(enrollSweestakeLocationRoute)
      ) {
        await navigate(
          '/Sweepstakes/' +
            globalThis.location.href
              .split('/Sweepstakes/')[1]
              .replace(enrollSweestakeLocationRoute, 'ParticipatingLocations')
        )
      }

      const selectedAccount = data?.corporateAccounts?.find(
        (acc) => acc.id === accountId
      )
      // reset data for enroll locations page
      qc.clear()

      if (selectedAccount) {
        setCurrentCorporateAccount(
          selectedAccount.name ?? 'Unnamed Account',
          selectedAccount.id
        )

        // when the user switches CA's using the select at the top left of the screen, clear the selected LE so data can be refreshed and not show the previous LE's data
        setPassedLe(undefined)

        setShowAllAccounts((prevShowAllAccounts) => !prevShowAllAccounts)
        handleClose()
      }
    },
    [data, navigate, qc, setCurrentCorporateAccount, handleClose, setPassedLe]
  )

  const sortedAccounts = useMemo(() => {
    return data?.corporateAccounts
      ?.slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }, [data])

  // Escape special regex characters in search term
  const escapeRegExp = (s: string) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)

  // filtered accounts for dropdown search (search by account name OR licensedEstablishments.standardName)
  const filteredAccounts = useMemo(() => {
    const accounts = data?.corporateAccounts ?? []
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      // If no search term, return all accounts sorted
      return accounts
        .slice()
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    }
    // Escape special regex characters
    const escaped = escapeRegExp(term)
    // Filter accounts based on search term
    return accounts
      .filter((acc) => {
        // Check CA name for exact match
        const nameMatch = new RegExp(escaped).exec(
          (acc.name ?? '').toLowerCase()
        )
        // Check associated LEs for any match
        const leMatch = data?.licensedEstablishments?.some(
          (le) =>
            le.corporateAccountId === acc.id && // only consider LEs linked to this CA
            (le.standardName ?? '').toLowerCase().includes(term)
        )
        // Return true if either matches
        return Boolean(nameMatch) || Boolean(leMatch)
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }, [data?.corporateAccounts, data?.licensedEstablishments, searchTerm])

  useEffect(() => {
    const firstAccount = sortedAccounts?.[0]

    if (firstAccount && currentCorporateAccountName === undefined) {
      setCurrentCorporateAccount(
        firstAccount.name ?? 'Unnamed Account',
        firstAccount.id
      )
    }
  }, [sortedAccounts, currentCorporateAccountName, setCurrentCorporateAccount])

  if (hasNoAccess) {
    return null
  }

  return (
    <button
      type="button"
      onClick={toggleMenu}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls="appbar-corporate-account-menu"
      className="flex flex-row justify-start gap-1 pl-6 border-0 bg-transparent p-0 text-left"
    >
      <Typography
        component={'span'}
        variant="body-2"
        display={['flex', 'flex', 'flex']}
        alignItems="center"
        sx={{
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              display: {
                xs: 'none',
                sm: 'none',
                md: 'none',
                lg: 'block',
              },
            }}
          >
            <span
              style={{
                borderRight: '1px solid #292B2699',
                height: '16px',
                marginRight: '22px',
              }}
            ></span>
          </Box>
        )}
        {isMobile &&
        currentCorporateAccountName &&
        currentCorporateAccountName.length > 15
          ? `${currentCorporateAccountName.substring(0, 15)}...`
          : currentCorporateAccountName ?? 'Select an Account'}
      </Typography>
      <Stack alignItems="center" p={1}>
        {open ? (
          <img
            alt="Closed Corporate Account Menu"
            src={ChevronUp}
            width="20"
            height="20"
          />
        ) : (
          <img
            alt="Opened Corporate Account Menu"
            src={ChevronUp}
            width="20"
            height="20"
            className="rotate-180"
          />
        )}
      </Stack>
      <StyledMenu
        id="appbar-corporate-account-menu"
        MenuListProps={{
          'aria-labelledby': 'demo-customized-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'center' : 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        }}
        transformOrigin={{
          vertical: isMobile ? 'top' : 'center',
          horizontal: isMobile ? 'center' : 'left',
        }}
        sx={{
          marginTop: isMobile ? '0' : '16px',
        }}
      >
        <List
          sx={{
            width: isMobile ? '300px' : '400px',
            maxHeight: '400px',
            overflow: 'auto',
            paddingY: 0,
          }}
        >
          {/* Search box shown at top of dropdown when open */}
          {open && (
            <ListItem
              sx={{
                paddingTop: 2,
                paddingBottom: 1,
              }}
              // prevent clicks inside the menu (search box) from bubbling up and toggling the menu
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                placeholder="Search accounts or licensed establishment"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                // also prevent the native input mousedown from bubbling
                inputProps={{
                  'aria-label': 'search-corporate-account',
                  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                }}
                // Add search icon at start of input
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{ color: 'text.secondary' }}
                    >
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                sx={{
                  // highlight the border and add focus/hover styles
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'black', // default highlight color
                      borderWidth: 1.5,
                    },
                    '&:hover fieldset': {
                      borderColor: 'black',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'black',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(204,32,39,0.08)',
                    },
                  },
                }}
              />
            </ListItem>
          )}

          {(filteredAccounts ?? []).map((account, index) => (
            <React.Fragment key={account.id.toString()}>
              {index > 0 && <Divider sx={{ width: '100%' }} />}
              <ListItem
                onClick={async (e) => {
                  e.stopPropagation()
                  await handleAccountSelect(account.id)
                }}
              >
                <ListItemButton
                  sx={{
                    backgroundColor:
                      account.id === currentCorporateAccountId
                        ? 'rgba(204, 32, 39, 0.08)'
                        : 'transparent',
                    paddingLeft: '1px',
                    paddingTop: '25px',
                    paddingBottom: '25px',
                    marginLeft: '-10px',
                    marginRight: '-10px',
                    height: '40px',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body-2"
                        sx={{
                          fontSize: '16px',
                          color:
                            showAllAccounts &&
                            account.id === currentCorporateAccountId
                              ? '#CC2027'
                              : '#2C2C27',
                          fontWeight:
                            showAllAccounts &&
                            account.id === currentCorporateAccountId
                              ? 'bold'
                              : 'normal',
                          mb: -1,
                        }}
                      >
                        {account.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '14px',
                          whiteSpace: 'pre-line',
                          color: '#2C2C27AD',
                        }}
                      >
                        #{account.id}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </StyledMenu>
    </button>
  )
}
